/**
 * Design Compiler — DETERMINISTIC. Converts one Design JSON concept (from
 * OpenAI) into the native iVisora Editor Document (template_json). OpenAI
 * never writes Fabric or editor JSON; this compiler owns that translation
 * so the output is always editor-consumable regardless of model quirks.
 *
 * It is defensive by design: AI output is imperfect, so geometry is clamped
 * into the canvas, unmappable objects are dropped, ids are made unique, and
 * the result is validated with the existing editorCompiler validator (editor
 * compatibility gate) before it is returned.
 */

import { validateEditorDocument } from '../editorCompiler';

const SHAPE_KINDS = new Set(['rect', 'roundedRect', 'circle', 'triangle', 'diamond', 'star', 'hexagon', 'pentagon']);
const ICON_EMOJI = { check: '✔️', star: '⭐', heart: '❤️', phone: '📞', mail: '✉️', location: '📍', logo: '🔷' };

const clampBox = (obj, canvas) => {
  const width = Math.max(1, Math.min(obj.width, canvas.width));
  const height = Math.max(1, Math.min(obj.height, canvas.height));
  const x = Math.max(0, Math.min(obj.x, canvas.width - width));
  const y = Math.max(0, Math.min(obj.y, canvas.height - height));
  return { x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) };
};

const applyEffects = (el, obj) => {
  if (obj.shadow) {
    el.shadowOffsetX = obj.shadow.offsetX ?? 0;
    el.shadowOffsetY = obj.shadow.offsetY ?? 3;
    el.shadowBlur = obj.shadow.blur ?? 8;
    el.shadowColor = obj.shadow.color ?? 'rgba(0,0,0,0.2)';
  }
  if (obj.gradient?.colors?.length >= 2) {
    el.fillGradient = { colors: [...obj.gradient.colors], angle: obj.gradient.angle ?? 90 };
  }
  if (obj.stroke?.color) {
    el.stroke = obj.stroke.color;
    el.strokeWidth = obj.stroke.width ?? 1;
  }
  if (obj.blendMode && obj.blendMode !== 'normal') el.blendMode = obj.blendMode;
  if (typeof obj.opacity === 'number') el.opacity = obj.opacity;
  return el;
};

const textProps = (obj, colors) => ({
  text: obj.text || '',
  fontSize: Math.max(6, Math.round(obj.fontSize || 32)),
  fontFamily: obj.fontFamily || 'Inter',
  fontWeight: obj.fontWeight || 400,
  color: obj.color || colors.text || '#111111',
  textAlign: ['left', 'center', 'right'].includes(obj.align) ? obj.align : 'left',
  lineHeight: typeof obj.lineHeight === 'number' ? obj.lineHeight : 1.2,
  letterSpacing: typeof obj.letterSpacing === 'number' ? obj.letterSpacing : 0,
});

/**
 * Compile one Design JSON concept into an Editor Document.
 * Returns { canvas_width, canvas_height, metadata, pages: [{ id, name,
 * background, elements }] }, validated for editor compatibility.
 * Throws only if a non-empty design cannot yield a single valid element.
 */
export const compileDesignJsonToEditorDocument = (design, brief = {}, index = 0) => {
  const canvas = {
    width: Math.round(design.canvas?.width || brief.canvas?.width || 1080),
    height: Math.round(design.canvas?.height || brief.canvas?.height || 1080),
  };
  const colors = design.colors || {};
  const background = colors.background || design.canvas?.background || '#ffffff';

  const usedIds = new Set();
  const uniqueId = (raw, fallback) => {
    let id = raw && !usedIds.has(raw) ? String(raw) : fallback;
    while (usedIds.has(id)) id = `${fallback}_${Math.random().toString(36).slice(2, 6)}`;
    usedIds.add(id);
    return id;
  };

  const commonMeta = (obj) => ({
    editable: obj.editable === false
      ? { content: false, style: true, position: false, delete: false }
      : { content: true, style: true, position: true, delete: true },
    role: obj.role || null,
    generator: 'aiTemplateGenerator/openai',
  });

  const elements = [];
  const groupBuckets = new Map(); // groupId -> [elementIds]

  const ordered = [...design.objects]
    .map((obj, i) => ({ obj, order: typeof obj.z === 'number' ? obj.z : i }))
    .sort((a, b) => a.order - b.order)
    .map((x) => x.obj);

  const pushEl = (el, groupId) => {
    elements.push(el);
    if (groupId) {
      if (!groupBuckets.has(groupId)) groupBuckets.set(groupId, []);
      groupBuckets.get(groupId).push(el.id);
    }
  };

  ordered.forEach((obj, i) => {
    if (![obj.x, obj.y, obj.width, obj.height].every((n) => typeof n === 'number' && Number.isFinite(n))) return;
    const box = clampBox(obj, canvas);
    const base = { ...box, rotation: obj.rotation || 0, opacity: 1, locked: false, visible: true, parentId: null, meta: commonMeta(obj) };
    const groupId = obj.group || null;

    if (obj.type === 'text') {
      pushEl(applyEffects({ ...base, id: uniqueId(obj.id, `text_${i}`), type: 'text', ...textProps(obj, colors) }, obj), groupId);
      return;
    }
    if (obj.type === 'shape') {
      pushEl(applyEffects({
        ...base, id: uniqueId(obj.id, `shape_${i}`), type: 'shape',
        shape: SHAPE_KINDS.has(obj.shape) ? obj.shape : 'rect',
        fill: obj.fill || colors.primary || '#6366f1',
        stroke: 'none', strokeWidth: 0,
        cornerRadius: obj.cornerRadius || 0, fillOpacity: typeof obj.opacity === 'number' ? obj.opacity : 1,
      }, obj), groupId);
      return;
    }
    if (obj.type === 'button' || obj.type === 'badge') {
      // A button/badge is a shape + centered label — grouped so they move together.
      const gid = groupId || `${obj.type}_${i}`;
      const shapeId = uniqueId(`${obj.id || obj.type + i}_bg`, `${obj.type}_${i}_bg`);
      pushEl(applyEffects({
        ...base, id: shapeId, type: 'shape',
        shape: obj.type === 'button' ? 'roundedRect' : (SHAPE_KINDS.has(obj.shape) ? obj.shape : 'roundedRect'),
        fill: obj.fill || colors.accent || colors.primary || '#111111',
        stroke: 'none', strokeWidth: 0,
        cornerRadius: obj.cornerRadius ?? (obj.type === 'button' ? Math.round(box.height / 2) : 8),
        fillOpacity: 1,
      }, obj), gid);
      pushEl({
        ...base, id: uniqueId(`${obj.id || obj.type + i}_label`, `${obj.type}_${i}_label`), type: 'text',
        ...textProps({ ...obj, align: obj.align || 'center', color: obj.color || colors.background || '#ffffff' }, colors),
      }, gid);
      return;
    }
    if (obj.type === 'icon') {
      pushEl({
        ...base, id: uniqueId(obj.id, `icon_${i}`), type: 'sticker',
        emoji: ICON_EMOJI[obj.icon] || '⬢',
        fontSize: Math.round(Math.min(box.width, box.height) * 0.8),
        meta: { ...base.meta, iconName: obj.icon || null, iconColor: obj.iconColor || null },
      }, groupId);
      return;
    }
    if (obj.type === 'image') {
      // Placeholder → native editor frame (gray drop-zone) carrying the query;
      // a real url (future) → image element.
      if (obj.url) {
        pushEl(applyEffects({ ...base, id: uniqueId(obj.id, `image_${i}`), type: 'image', src: obj.url, objectFit: obj.fit || 'cover' }, obj), groupId);
      } else {
        pushEl(applyEffects({
          ...base, id: uniqueId(obj.id, `frame_${i}`), type: 'frame', shape: 'rectangle',
          fit: obj.fit || 'cover', image: null,
          meta: { ...base.meta, imageQuery: obj.query || null },
        }, obj), groupId);
      }
    }
  });

  // Insert group elements just above their topmost member (editor convention).
  groupBuckets.forEach((memberIds, groupId) => {
    if (memberIds.length < 2) return; // a single member needs no group
    const indexes = memberIds.map((id) => elements.findIndex((el) => el.id === id)).filter((i) => i >= 0);
    memberIds.forEach((id) => {
      const idx = elements.findIndex((el) => el.id === id);
      if (idx >= 0) elements[idx] = { ...elements[idx], parentId: `group_${groupId}` };
    });
    elements.splice(Math.max(...indexes) + 1, 0, {
      id: `group_${groupId}`, type: 'group', name: `Group ${groupId}`,
      parentId: null, visible: true, locked: false, derivedBounds: null,
      meta: { generator: 'aiTemplateGenerator/openai' },
    });
  });

  const doc = {
    canvas_width: canvas.width,
    canvas_height: canvas.height,
    metadata: {
      source: 'openai',
      concept: design.concept || `Concept ${index + 1}`,
      theme: design.theme?.name || null,
      documentType: brief.documentType || null,
      compiler: 'aiTemplateGenerator/openai/designCompiler',
    },
    pages: [{ id: `openai-page-${index + 1}-${Date.now().toString(36)}`, name: 'Page 1', background, elements }],
  };

  const { valid, errors } = validateEditorDocument(doc);
  if (!valid) throw new Error(`Design JSON did not compile to a valid editor document: ${errors.join('; ')}`);
  return doc;
};
